import React, { useState, useRef } from 'react';
import {
  CheckCircleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from '@heroicons/react/24/outline';
import { PROCTORING_EYE_GAZE_CRITERIA } from '../../screenings/screeningConstants';
import SessionVideoPlayer from '../../../components/results/SessionVideoPlayer';

function computeTotalFromCategories(cats) {
  if (!cats) return null;
  const entries = Object.entries(cats).filter(([k]) => !k.startsWith('_'));
  if (entries.length === 0) return null;
  return Math.round(entries.reduce((sum, [, c]) => sum + (c?.score || 0), 0));
}

export default function InterviewProctoringContent({ data, session, context = 'interview' }) {
  const isAssessment = context === 'assessment';
  const [showDetailedAnalysis, setShowDetailedAnalysis] = useState(false);
  const [selectedProctoringCategory, setSelectedProctoringCategory] = useState(null);
  const scoreBreakdownRef = useRef(null);
  const categoryTotal = computeTotalFromCategories(data?.categories);
  const consistentRiskScore = categoryTotal ?? data?.riskScore ?? 0;

  return (
    <div className="space-y-6">
      {!data ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          <span className="ml-3 text-gray-600">Loading proctoring data...</span>
        </div>
      ) : (
        <>
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
            {/* Body */}
            <div className="px-6 py-6">
              {/* ── Premium Stats Header ── */}
              {(() => {
                const score = consistentRiskScore;
                const riskColor = score >= 100 ? '#dc2626' : score >= 50 ? '#ea580c' : '#059669';
                const gaugeRadius = 72;
                const gaugeStroke = 12;
                const gaugeCx = 90;
                const gaugeCy = 82;
                const semiCircumference = Math.PI * gaugeRadius;
                const maxScore = 75;
                const gaugePct = Math.min(score / maxScore, 1);
                const gaugeOffset = semiCircumference - (gaugePct * semiCircumference);
                return (
                  <div className="flex flex-col xl:flex-row gap-5 xl:items-stretch mb-6">
                    {/* Left Column: Video Player (only if session is provided or context is assessment) */}
                    {(session || isAssessment) && (
                      <div className="w-full xl:basis-[40%] xl:flex-none flex-shrink-0">
                        <SessionVideoPlayer
                          session={session || data} // for assessment, data IS the session
                          context={context}
                        />
                      </div>
                    )}
                    
                    {/* Right Column (or Full Width): Premium Stats */}
                    <div className={`w-full ${session || isAssessment ? 'xl:basis-[60%]' : ''} flex flex-col gap-5`}>
                      {/* Gauge Meter & Metric Cards wrapped in a clean container */}
                      <div className="bg-gradient-to-br from-white to-slate-50 border border-gray-200 rounded-2xl p-5 shadow-sm flex flex-col sm:flex-row items-center gap-6 h-full">
                        {/* Gauge Meter */}
                        <div className="flex-shrink-0 relative" style={{ width: 180, height: 110 }}>
                          <svg width="180" height="110" viewBox="0 0 180 110">
                            <defs>
                              <linearGradient id="gaugeGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                                <stop offset="0%" stopColor="#10b981" />
                                <stop offset="30%" stopColor="#f59e0b" />
                                <stop offset="60%" stopColor="#f97316" />
                                <stop offset="100%" stopColor="#ef4444" />
                              </linearGradient>
                              <filter id="gaugeShadow" x="-10%" y="-10%" width="120%" height="120%">
                                <feDropShadow dx="0" dy="1" stdDeviation="2" floodColor={riskColor} floodOpacity="0.25" />
                              </filter>
                            </defs>
                            {/* Background track */}
                            <path
                              d={`M ${gaugeCx - gaugeRadius} ${gaugeCy} A ${gaugeRadius} ${gaugeRadius} 0 0 1 ${gaugeCx + gaugeRadius} ${gaugeCy}`}
                              fill="none"
                              stroke="#f1f5f9"
                              strokeWidth={gaugeStroke}
                              strokeLinecap="round"
                            />
                            {/* Filled arc */}
                            <path
                              d={`M ${gaugeCx - gaugeRadius} ${gaugeCy} A ${gaugeRadius} ${gaugeRadius} 0 0 1 ${gaugeCx + gaugeRadius} ${gaugeCy}`}
                              fill="none"
                              stroke="url(#gaugeGradient)"
                              strokeWidth={gaugeStroke}
                              strokeLinecap="round"
                              strokeDasharray={semiCircumference}
                              strokeDashoffset={gaugeOffset}
                              filter="url(#gaugeShadow)"
                              style={{ transition: 'stroke-dashoffset 1s cubic-bezier(0.4, 0, 0.2, 1)' }}
                            />
                            {/* Needle dot at the end of arc */}
                            {score > 0 && (() => {
                              const angle = Math.PI - (gaugePct * Math.PI);
                              const dotX = gaugeCx + gaugeRadius * Math.cos(angle);
                              const dotY = gaugeCy - gaugeRadius * Math.sin(angle);
                              return (
                                <circle
                                  cx={dotX} cy={dotY} r={gaugeStroke / 2 + 2}
                                  fill="white" stroke={riskColor} strokeWidth="3"
                                  style={{ filter: `drop-shadow(0 1px 3px ${riskColor}60)`, transition: 'all 1s cubic-bezier(0.4, 0, 0.2, 1)' }}
                                />
                              );
                            })()}
                          </svg>
                          {/* Score text in center of gauge */}
                          <div className="absolute" style={{ bottom: 6, left: '50%', transform: 'translateX(-50%)' }}>
                            <div className="flex flex-col items-center">
                              <span className="text-3xl font-black tracking-tighter leading-none" style={{ color: riskColor }}>{score}</span>
                              <span className="text-[9px] text-gray-400 font-semibold uppercase tracking-widest mt-0.5">Risk Score</span>
                            </div>
                          </div>
                        </div>
                        
                        {/* Metric Cards - Stacked vertically */}
                        <div className="flex-1 flex flex-col gap-3 w-full">
                          {/* Decision Card */}
                          <div className="rounded-xl border border-gray-100 bg-white/60 px-5 py-4 flex items-center gap-4">
                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${data.riskLevel === 'Compliant' ? 'bg-emerald-100' : 'bg-orange-100'}`}>
                              {data.riskLevel === 'Compliant' ? (
                                <svg className="w-5 h-5 text-emerald-600" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                              ) : (
                                <svg className="w-5 h-5 text-orange-600" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                                </svg>
                              )}
                            </div>
                            <div>
                              <p className="text-[10px] text-gray-400 font-semibold uppercase tracking-widest mb-1">Decision</p>
                              <p className={`text-base font-bold leading-tight ${data.riskLevel === 'Compliant' ? 'text-emerald-600' : 'text-orange-600'}`}>
                                {data.riskLevel === 'Compliant' ? 'Not Flagged' : 'Flagged'}
                              </p>
                              <p className="text-[11px] text-gray-400 mt-0.5">
                                {data.riskLevel === 'Compliant'
                                  ? 'Session passed all integrity checks'
                                  : `${(data.flagReasonsInEnglish || []).length} issue${(data.flagReasonsInEnglish || []).length !== 1 ? 's' : ''} detected`}
                              </p>
                            </div>
                          </div>
                          
                          {/* Risk Level Card */}
                          <div className="rounded-xl border border-gray-100 bg-white/60 px-5 py-4 flex items-center gap-4">
                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${data.riskLevel === 'High Risk' ? 'bg-red-100' : data.riskLevel === 'Flagged' ? 'bg-orange-100' : 'bg-emerald-100'}`}>
                              <svg className={`w-5 h-5 ${data.riskLevel === 'High Risk' ? 'text-red-600' : data.riskLevel === 'Flagged' ? 'text-orange-600' : 'text-emerald-600'}`} fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
                              </svg>
                            </div>
                            <div>
                              <p className="text-[10px] text-gray-400 font-semibold uppercase tracking-widest mb-1">Risk Level</p>
                              <span
                                className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-bold ${data.riskLevel === 'High Risk' ? 'bg-red-50 text-red-600 ring-1 ring-red-200'
                                  : data.riskLevel === 'Flagged' ? 'bg-orange-50 text-orange-600 ring-1 ring-orange-200'
                                    : 'bg-emerald-50 text-emerald-600 ring-1 ring-emerald-200'
                                  }`}
                              >
                                <span className={`w-1.5 h-1.5 rounded-full ${data.riskLevel === 'High Risk' ? 'bg-red-500'
                                  : data.riskLevel === 'Flagged' ? 'bg-orange-500'
                                    : 'bg-emerald-500'
                                  }`} style={{ boxShadow: `0 0 6px ${riskColor}50` }} />
                                {data.riskLevel}
                              </span>
                              <p className="text-[11px] text-gray-400 mt-1">
                                {data.riskLevel === 'High Risk'
                                  ? 'Immediate review recommended'
                                  : data.riskLevel === 'Flagged'
                                    ? 'Review at your discretion'
                                    : 'No action needed'}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}
              {/* ── AI Analysis — Dynamic, light indigo themed ── */}
              {(() => {
                const reasons = data.flagReasonsInEnglish || [];
                const score = consistentRiskScore;
                const cats = data.categories || {};
                const catEntries = Object.entries(cats).filter(([, c]) => c != null);
                const totalCount = catEntries.length;
                // Use AI-generated summary from backend if available
                const aiSummary = data.aiSummary;
                const hasAiSummary = aiSummary && aiSummary.summary;
                // Fallback summary if AI is unavailable
                const fallbackSummary = score === 0
                  ? `This candidate's ${isAssessment ? 'assessment' : 'session'} was analyzed across ${totalCount} proctoring dimensions. All monitored signals remained within expected thresholds.`
                  : reasons.length > 0
                    ? `Flagged ${reasons.length} integrity check${reasons.length > 1 ? 's' : ''} with a cumulative risk score of ${score}. Detailed review recommended.`
                    : `A cumulative risk score of ${score} was generated based on session monitoring. Detailed review recommended.`;
                // Render markdown bold (**text**) as <strong>
                const renderBold = (text) => {
                  if (!text) return null;
                  const parts = text.split(/(\*\*[^*]+\*\*)/g);
                  return parts.map((part, i) => {
                    if (part.startsWith('**') && part.endsWith('**')) {
                      return <strong key={i} className="font-semibold text-gray-900">{part.slice(2, -2)}</strong>;
                    }
                    return part;
                  });
                };
                // Severity color mapping
                const severityStyles = {
                  Critical: { color: '#dc2626', bg: '#fef2f2' },
                  High: { color: '#ea580c', bg: '#fff7ed' },
                  Medium: { color: '#d97706', bg: '#fffbeb' },
                  Low: { color: '#65a30d', bg: '#f7fee7' },
                };
                // Build findings list – prefer AI findings, fallback to flagReasons
                const findings = hasAiSummary && aiSummary.findings?.length > 0
                  ? aiSummary.findings
                  : reasons.map(r => {
                    const lower = r.toLowerCase();
                    let severity = 'Medium';
                    if (lower.includes('identity') || lower.includes('phone')) severity = 'Critical';
                    else if (lower.includes('ai-assisted') || lower.includes('multiple')) severity = 'High';
                    return { text: r, severity };
                  });
                return (
                  <div className="mb-1">
                    {/* Header */}
                    <div className="flex items-center justify-between rounded-t-xl bg-blue-50 border border-blue-100 border-b-0 px-5 py-3">
                      <div className="flex items-center gap-2.5">
                        <div className="w-7 h-7 rounded-lg bg-blue-600 flex items-center justify-center shadow-sm">
                          <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456z" />
                          </svg>
                        </div>
                        <div>
                          <h4 className="text-sm font-bold text-gray-900 tracking-tight leading-none">{isAssessment ? 'Assessment Proctoring Summary' : 'Interview Summary'}</h4>
                        </div>
                      </div>
                      <button
                        onClick={() =>
                          setShowDetailedAnalysis(prev => {
                            const next = !prev;
                            if (next) {
                              setSelectedProctoringCategory('browserActivity');
                            }
                            return next;
                          })
                        }
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all duration-200 bg-white border border-blue-200 text-blue-700 hover:bg-blue-50 hover:border-blue-300 shadow-sm"
                      >
                        {showDetailedAnalysis ? (
                          <>
                            <ChevronUpIcon className="w-3.5 h-3.5" />
                            Hide advanced details
                          </>
                        ) : (
                          <>
                            <ChevronDownIcon className="w-3.5 h-3.5" />
                            View advanced details
                          </>
                        )}
                      </button>
                    </div>
                    {/* Body */}
                    <div className="rounded-b-xl border border-blue-100 bg-white px-5 py-4">
                      {/* AI-Generated Summary */}
                      <div className="mb-4">
                        <p className="text-[13px] text-gray-600 leading-relaxed">
                          {hasAiSummary ? renderBold(aiSummary.summary) : fallbackSummary}
                        </p>
                      </div>
                      {/* Findings */}
                      {findings.length > 0 ? (
                        <div>
                          <p className="text-[10px] text-gray-500 font-semibold uppercase tracking-widest mb-2">Key Observations</p>
                          <div className="space-y-2">
                            {findings.slice(0, 3).map((finding, idx) => {
                              const sev = severityStyles[finding.severity] || severityStyles.Medium;
                              return (
                                <div key={idx} className="flex items-start gap-2.5 rounded-lg border border-gray-100 px-3 py-2.5">
                                  <div className="w-2 h-2 mt-1.5 rounded-full bg-blue-500" />
                                  <p className="text-[13px] text-gray-700 leading-relaxed flex-1">
                                    {renderBold(finding.text)}
                                  </p>
                                  <span
                                    className="flex-shrink-0 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide mt-0.5"
                                    style={{ color: sev.color, backgroundColor: sev.bg }}
                                  >
                                    {finding.severity}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ) : score === 0 ? (
                        <div className="flex items-center gap-3 rounded-xl bg-emerald-50 border border-emerald-100 px-4 py-3.5">
                          <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                            <CheckCircleIcon className="w-5 h-5 text-emerald-600" />
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-emerald-800">All checks passed</p>
                            <p className="text-xs text-emerald-600 mt-0.5">No integrity violations were detected during this session.</p>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center gap-3 rounded-xl bg-orange-50 border border-orange-100 px-4 py-3.5">
                          <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                            <svg className="w-5 h-5 text-orange-600" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                               <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-orange-800">Review Recommended</p>
                            <p className="text-xs text-orange-600 mt-0.5">Potential anomalies detected across monitoring signals.</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
          {showDetailedAnalysis && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" style={{ animation: 'fadeSlideIn 0.3s ease-out' }}>
              {/* Left Column: Score Breakdown only (risk summary card removed) */}
              <div className="lg:col-span-1 space-y-4" ref={scoreBreakdownRef}>
                {/* Score Breakdown */}
                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
                  <div className="px-5 py-4">
                    <h4 className="text-sm font-semibold text-gray-900 mb-3">Score Breakdown</h4>
                    <div className="space-y-3">
                      {[
                        {
                          label: 'Environment (Browser & Setup)',
                          key: 'environment',
                          keys: ['browserActivity', 'transcriptAnalysis', 'responseDelay'],
                        },
                        {
                          label: 'Audio Signals',
                          key: 'audio',
                          keys: ['speechPattern', 'speakerDetection'],
                        },
                        {
                          label: 'Video Signals',
                          key: 'video',
                          keys: isAssessment
                            ? [
                                'videoIdentity',
                                'videoMultipleFaces',
                                'videoPhoneDetection',
                              ]
                            : [
                                'videoPresence',
                                'videoMultipleFaces',
                                'videoIdentity',
                                'videoEyeGaze',
                                'videoHeadPose',
                                'videoPhoneDetection',
                              ],
                        },
                      ].map(section => {
                        const cats = data.categories || {};
                        const hasAny = section.keys.some(k => cats[k]);
                        if (!hasAny) return null;
                        return (
                          <div key={section.key} className="space-y-1.5">
                            <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-[0.14em]">
                              {section.label}
                            </p>
                            {section.keys.map(key => {
                              const cat = cats[key];
                              if (!cat) return null;
                              const isSelected = selectedProctoringCategory === key;
                              const clickableKeys = [
                                'browserActivity',
                                'transcriptAnalysis',
                                'responseDelay',
                                'speechPattern',
                                'speakerDetection',
                                'videoPresence',
                                'videoMultipleFaces',
                                'videoIdentity',
                                'videoEyeGaze',
                                'videoHeadPose',
                                'videoPhoneDetection',
                              ];
                              const clickable = clickableKeys.includes(key);
                              return (
                                <button
                                  key={key}
                                  type="button"
                                  onClick={() => setSelectedProctoringCategory(clickable ? key : null)}
                                  className={`w-full flex items-center justify-between text-sm rounded-lg px-2 py-1.5 transition-colors ${clickable
                                    ? isSelected
                                      ? 'bg-indigo-50 text-indigo-900'
                                      : 'hover:bg-gray-50 text-gray-800'
                                    : 'cursor-default text-gray-700'
                                    }`}
                                >
                                  <span className="capitalize truncate">
                                    {key.replace(/([A-Z])/g, ' $1').trim()}
                                  </span>
                                  <span
                                    className={`ml-3 inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${cat.score > 0
                                      ? 'bg-rose-50 text-rose-600 ring-1 ring-rose-100'
                                      : 'bg-emerald-50 text-emerald-600 ring-1 ring-emerald-100'
                                      }`}
                                  >
                                    +{cat.score} pts
                                  </span>
                                </button>
                              );
                            })}
                          </div>
                        );
                      })}
                      <div className="flex items-center justify-between text-sm pt-2 border-t border-gray-200 mt-3">
                        <span className="font-semibold text-gray-900">Total</span>
                        <span className="font-bold text-gray-900">
                          {consistentRiskScore} pts
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* Right Column: Detailed Categories (grouped into Environment, Audio, Video) */}
              <div className="lg:col-span-2 space-y-4 transition-all duration-300 ease-out">
                {selectedProctoringCategory === 'browserActivity' &&
                  data.categories?.browserActivity ? (
                  /* Browser Activity focused card */
                  <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden transform transition-all duration-300 ease-out">
                    <div className="px-5 py-4 border-b border-gray-100 bg-gradient-to-r from-indigo-50/80 via-white to-white flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl bg-indigo-600 flex items-center justify-center shadow-sm">
                          <svg
                            className="w-4.5 h-4.5 text-white"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M3.75 6.75h16.5M3.75 9.75h10.5M3.75 12.75h7.5M3.75 15.75h4.5M16.5 9.75h3.75m-3.75 3h3.75m-3.75 3h3.75"
                            />
                          </svg>
                        </div>
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-[0.12em] text-gray-500">
                            Environment Detail
                          </p>
                          <h2 className="text-base font-bold text-gray-900 leading-tight">
                            Browser Activity Breakdown
                          </h2>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <p className="text-[11px] text-gray-500 font-semibold uppercase tracking-[0.14em]">
                            Contribution
                          </p>
                          <p className="text-sm font-bold text-gray-900">
                            +{data.categories.browserActivity.score} pts
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="px-5 pt-4 pb-5 bg-gray-50/40">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                        <div className="col-span-2 rounded-xl border border-gray-100 bg-white px-4 py-3.5">
                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-[0.14em] mb-1.5">
                            Overview
                          </p>
                          <p className="text-sm text-gray-700">
                            We analyze tab focus, fullscreen behaviour, and navigation events to detect potential
                            external help or distractions during the {isAssessment ? 'assessment' : 'interview'}.
                          </p>
                        </div>
                        <div className="rounded-xl border border-gray-100 bg-white px-4 py-3.5 flex flex-col justify-center">
                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-[0.14em] mb-1.5">
                            Total Violations
                          </p>
                          <p className="text-lg font-bold text-gray-900">
                            {data.categories.browserActivity.items?.reduce(
                              (sum, item) => sum + (item.count || 0),
                              0
                            ) || 0}
                          </p>
                        </div>
                      </div>
                      <div className="rounded-xl border border-gray-100 bg-white overflow-hidden">
                        <div className="px-4 pt-3 pb-2 border-b border-gray-100 flex items-center justify-between">
                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-[0.14em]">
                            Violation Breakdown
                          </p>
                          <span className="text-[11px] text-gray-500">
                            Each row: Count × Pts each = Total
                          </span>
                        </div>
                        <div className="px-4 pb-3 pt-1">
                          <table className="min-w-full divide-y divide-gray-100">
                            <thead>
                              <tr>
                                <th className="text-left text-xs font-semibold text-gray-500 uppercase pb-2">
                                  Violation
                                </th>
                                <th className="text-right text-xs font-semibold text-gray-500 uppercase pb-2">
                                  Count
                                </th>
                                <th className="text-right text-xs font-semibold text-gray-500 uppercase pb-2">
                                  Pts Each
                                </th>
                                <th className="text-right text-xs font-semibold text-gray-500 uppercase pb-2">
                                  Total
                                </th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                              {data.categories.browserActivity.items?.map((item, idx) => (
                                <tr key={idx}>
                                  <td className="py-2.5 text-sm text-gray-900">{item.label}</td>
                                  <td className="py-2.5 text-sm text-gray-600 text-right">{item.count}</td>
                                  <td className="py-2.5 text-sm text-gray-600 text-right">{item.pointsEach}</td>
                                  <td className="py-2.5 text-sm font-semibold text-gray-900 text-right">
                                    {item.points}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          {/* Calculated Criteria — dashed underline, tooltip on hover */}
                          <div className="group mt-4 relative inline-block cursor-help">
                            <span className="text-xs font-semibold text-gray-600 uppercase border-b border-dashed border-gray-500 pb-0.5">
                              Calculated Criteria
                            </span>
                            <div
                              role="tooltip"
                              className="pointer-events-none absolute left-0 bottom-full mb-1 z-20 w-80 max-w-[calc(100vw-2rem)] rounded-xl border border-gray-200 bg-gray-900 px-4 py-3 shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity duration-200"
                            >
                              <div className="absolute left-4 -bottom-1 h-2 w-2 rotate-45 border-r border-b border-gray-200 bg-gray-900" />
                              <p className="text-xs text-gray-100 mb-2 relative">
                                {data.categories.browserActivity.score === 0
                                  ? 'Browser Activity adds 0 points because no violations were detected. Total = Count × Pts each for each row (all 0 here).'
                                  : `Browser Activity total: ${data.categories.browserActivity.score} pts. Each row: Count × Pts each = Total. Sum of those = score.`}
                              </p>
                              <div className="space-y-0.5 relative">
                                {data.categories.browserActivity.items?.map((item, idx) => (
                                  <p key={idx} className="text-xs text-gray-300">
                                    <span className="font-medium text-gray-100">{item.label}</span>{' '}
                                    {item.count} × {item.pointsEach} = {item.points} pts
                                  </p>
                                ))}
                              </div>
                              <p className="text-xs text-gray-400 mt-2 relative">
                                Example: if Fullscreen Exit had count 4 and 2.5 pts each → 4 × 2.5 = 10 pts for that row.
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : selectedProctoringCategory === 'transcriptAnalysis' ? (
                  /* Transcript Analysis detailed card (HTML adapted from copy file) */
                  <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden transform transition-all duration-300 ease-out">
                    <div className="px-5 py-4 border-b border-gray-100 bg-gradient-to-r from-indigo-50/80 via-white to-white flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl bg-indigo-600 flex items-center justify-center shadow-sm">
                          <svg
                            className="w-4.5 h-4.5 text-white"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M4 5.5A2.5 2.5 0 016.5 3h11A2.5 2.5 0 0120 5.5v8A2.5 2.5 0 0117.5 16H9l-3.5 3 .5-3H6.5A2.5 2.5 0 014 13.5v-8z"
                            />
                          </svg>
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-[0.14em]">
                            AI-Assisted Signal
                          </p>
                          <h2 className="text-base font-bold text-gray-900 leading-tight">
                            Transcript Analysis Breakdown
                          </h2>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <p className="text-[11px] text-gray-500 font-semibold uppercase tracking-[0.14em]">
                            Contribution
                          </p>
                          <p className="text-sm font-bold text-gray-900">
                            +{(data.categories.transcriptAnalysis || {}).score ?? 0} pts
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="px-5 pt-4 pb-5 bg-gray-50/40 space-y-4">
                      {/* How this works — full-width card */}
                      <div className="rounded-xl border border-gray-100 bg-white px-4 py-3.5">
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-[0.14em] mb-1.5">
                          How this works
                        </p>
                        <p className="text-sm text-gray-700">
                          We use an AI model to look at wording patterns in answers and flag segments that
                          appear machine-generated or overly generic. This is a supporting signal and should
                          be reviewed alongside other evidence.
                        </p>
                      </div>
                      {/* Four metric cards on a single row (desktop) */}
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div className="rounded-xl border border-gray-100 bg-white px-4 py-3.5 text-center">
                          <p className="text-xs text-gray-500 font-semibold uppercase tracking-[0.14em] mb-1">
                            Samples
                          </p>
                          <p className="text-lg font-semibold text-gray-900">
                            {(data.categories.transcriptAnalysis || {}).details
                              ?.sampleCount ?? 0}
                          </p>
                        </div>
                        <div className="rounded-xl border border-gray-100 bg-white px-4 py-3.5 text-center">
                          <p className="text-xs text-gray-500 font-semibold uppercase tracking-[0.14em] mb-1">
                            Suspicious
                          </p>
                          <p className="text-lg font-semibold text-rose-600">
                            {(data.categories.transcriptAnalysis || {}).details
                              ?.suspiciousCount ?? 0}
                          </p>
                        </div>
                        <div className="rounded-xl border border-gray-100 bg-white px-4 py-3.5 text-center">
                          <p className="text-xs text-gray-500 font-semibold uppercase tracking-[0.14em] mb-1">
                            Suspicious Rate
                          </p>
                          <p className="text-lg font-semibold text-gray-900">
                            {(data.categories.transcriptAnalysis || {}).details
                              ?.suspiciousRate ?? 0}
                            %
                          </p>
                        </div>
                        <div className="rounded-xl border border-gray-100 bg-white px-4 py-3.5 text-center">
                          <p className="text-xs text-gray-500 font-semibold uppercase tracking-[0.14em] mb-1">
                            Pattern
                          </p>
                          <p
                            className={`text-sm font-semibold ${(data.categories.transcriptAnalysis || {}).details
                              ?.isPatternSuspicious
                              ? 'text-rose-600'
                              : 'text-emerald-600'
                              }`}
                          >
                            {(data.categories.transcriptAnalysis || {}).details
                              ?.isPatternSuspicious
                              ? 'Suspicious'
                              : 'Normal'}
                          </p>
                        </div>
                      </div>
                      {(data.categories.transcriptAnalysis || {}).perQuestion?.length >
                        0 && (
                          <div className="rounded-xl border border-gray-100 bg-white px-4 py-3.5 max-h-64 overflow-y-auto space-y-2">
                            <p className="text-xs font-semibold text-gray-700 uppercase tracking-[0.12em]">
                              Per-Question Analysis
                            </p>
                            {(data.categories.transcriptAnalysis || {}).perQuestion.map(
                              (q, idx) => (
                                <div
                                  key={idx}
                                  className={`p-3 rounded-lg border ${q.isSuspicious ? 'bg-rose-50 border-rose-200' : 'bg-gray-50 border-gray-200'
                                    }`}
                                >
                                  <div className="flex items-center justify-between mb-1.5">
                                    <span className="text-sm font-medium text-gray-900">
                                      Candidate Response {q.questionIndex + 1}
                                    </span>
                                    {q.isSuspicious && (
                                      <span className="text-xs font-medium text-rose-600">Flagged</span>
                                    )}
                                  </div>
                                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                                    <div>
                                      <span className="text-gray-500">AI Likeness:</span>{' '}
                                      <span
                                        className={`font-medium ${(q.aiLikeness ?? 0) > 0.7 ? 'text-rose-600' : 'text-gray-900'
                                          }`}
                                      >
                                        {q.aiLikeness != null
                                          ? Math.round(q.aiLikeness * 100) + '%'
                                          : '—'}
                                      </span>
                                    </div>
                                    <div>
                                      <span className="text-gray-500">Genericness:</span>{' '}
                                      <span className="font-medium text-gray-900">
                                        {q.genericness != null
                                          ? Math.round(q.genericness * 100) + '%'
                                          : '—'}
                                      </span>
                                    </div>
                                    <div>
                                      <span className="text-gray-500">Structure:</span>{' '}
                                      <span className="font-medium text-gray-900">
                                        {q.structureDensity != null
                                          ? Math.round(q.structureDensity * 100) + '%'
                                          : '—'}
                                      </span>
                                    </div>
                                    <div>
                                      <span className="text-gray-500">Real Exp:</span>{' '}
                                      <span
                                        className={`font-medium ${(q.realExperience ?? 0) < 0.3 ? 'text-rose-600' : 'text-emerald-600'
                                          }`}
                                      >
                                        {q.realExperience != null
                                          ? Math.round(q.realExperience * 100) + '%'
                                          : '—'}
                                      </span>
                                    </div>
                                  </div>
                                  {q.notes && (
                                    <p className="text-xs text-gray-600 mt-2 italic">{q.notes}</p>
                                  )}
                                </div>
                              )
                            )}
                          </div>
                        )}
                      <div className="group inline-block relative cursor-help">
                        <span className="text-xs font-semibold text-gray-600 uppercase border-b border-dashed border-gray-500 pb-0.5">
                          Calculated Criteria
                        </span>
                        <div
                          role="tooltip"
                          className="pointer-events-none absolute left-0 bottom-full mb-1 z-20 w-80 max-w-[calc(100vw-2rem)] rounded-xl border border-gray-200 bg-gray-900 px-4 py-3 shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity duration-200"
                        >
                          <div className="absolute left-4 -bottom-1 h-2 w-2 rotate-45 border-r border-b border-gray-200 bg-gray-900" />
                          {(() => {
                            const details =
                              (data.categories.transcriptAnalysis || {}).details || {};
                            const sampleCount = details.sampleCount ?? 0;
                            const suspiciousCount = details.suspiciousCount ?? 0;
                            const suspiciousRatePercent = details.suspiciousRate ?? 0;
                            const suspiciousRate = (suspiciousRatePercent || 0) / 100;
                            const baseFromCount = suspiciousCount * 1.5;
                            const curveFromRate = Math.pow(suspiciousRate || 0, 2) * 35;
                            const rawScore = baseFromCount + curveFromRate;
                            const roundedScore = Math.round(rawScore);
                            if (!sampleCount) {
                              return (
                                <p className="text-xs text-gray-100 relative">
                                  AI-assisted score is 0 because there were no answers available to analyse.
                                </p>
                              );
                            }
                            if (!suspiciousCount) {
                              return (
                                <p className="text-xs text-gray-100 relative">
                                  All {sampleCount} analysed answers looked human. With 0 suspicious answers,
                                  the AI-assisted score stays at 0 pts.
                                </p>
                              );
                            }
                            return (
                              <div className="space-y-1.5 text-xs text-gray-100 relative">
                                <p className="font-semibold text-gray-50">
                                  How we reached {roundedScore} pts:
                                </p>
                                <p>
                                  • Suspicious answers: {suspiciousCount} × 1.5 pts ={' '}
                                  <span className="font-semibold">{baseFromCount.toFixed(1)} pts</span>
                                </p>
                                <p>
                                  • Suspicious rate curve: ({(suspiciousRate * 100).toFixed(0)}% ÷ 100)² × 35
                                  ≈ <span className="font-semibold">{curveFromRate.toFixed(1)} pts</span>
                                </p>
                                <p>
                                  • Total: {baseFromCount.toFixed(1)} + {curveFromRate.toFixed(1)} ≈{' '}
                                  <span className="font-semibold">{rawScore.toFixed(1)} pts</span> → rounded
                                  to <span className="font-semibold">{roundedScore} pts</span> shown above.
                                </p>
                                <p className="text-gray-300">
                                  In short, more AI-like answers and a higher suspicious rate push this score
                                  up.
                                </p>
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                    </div>
                  </div>
                ) : selectedProctoringCategory === 'responseDelay' &&
                  data.categories?.responseDelay ? (
                  /* Response Timing detailed card */
                  <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden transform transition-all duration-300 ease-out">
                    <div className="px-5 py-4 border-b border-gray-100 bg-gradient-to-r from-indigo-50/80 via-white to-white flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl bg-indigo-600 flex items-center justify-center shadow-sm">
                          <svg
                            className="w-4.5 h-4.5 text-white"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M12 6v6l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-[0.14em]">
                            Behaviour Signal
                          </p>
                          <h2 className="text-base font-bold text-gray-900 leading-tight">
                            Response Timing Breakdown
                          </h2>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <p className="text-[11px] text-gray-500 font-semibold uppercase tracking-[0.14em]">
                            Contribution
                          </p>
                          <p className="text-sm font-bold text-gray-900">
                            +{data.categories.responseDelay.score} pts
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="px-5 pt-4 pb-5 bg-gray-50/40 space-y-4">
                      <div className="rounded-xl border border-gray-100 bg-white px-4 py-3.5">
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-[0.14em] mb-1.5">
                          How this works
                        </p>
                        <p className="text-sm text-gray-700">
                          We look at how long the candidate waits before answering after each question ends.
                          Very similar delays or extremely long pauses can indicate scripted answers or
                          external help.
                        </p>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div className="rounded-xl border border-gray-100 bg-white px-4 py-3.5 text-center">
                          <p className="text-xs text-gray-500 font-semibold uppercase tracking-[0.14em] mb-1">
                            Samples
                          </p>
                          <p className="text-lg font-semibold text-gray-900">
                            {data.categories.responseDelay.details?.sampleCount ?? 0}
                          </p>
                        </div>
                        <div className="rounded-xl border border-gray-100 bg-white px-4 py-3.5 text-center">
                          <p className="text-xs text-gray-500 font-semibold uppercase tracking-[0.14em] mb-1">
                            Mean Delay
                          </p>
                          <p className="text-lg font-semibold text-gray-900">
                            {data.categories.responseDelay.details?.meanDelayMs != null
                              ? `${data.categories.responseDelay.details.meanDelayMs}ms`
                              : '—'}
                          </p>
                        </div>
                        <div className="rounded-xl border border-gray-100 bg-white px-4 py-3.5 text-center">
                          <p className="text-xs text-gray-500 font-semibold uppercase tracking-[0.14em] mb-1">
                            Std Dev
                          </p>
                          <p className="text-lg font-semibold text-gray-900">
                            {data.categories.responseDelay.details?.stdDevDelayMs != null
                              ? `${data.categories.responseDelay.details.stdDevDelayMs}ms`
                              : '—'}
                          </p>
                        </div>
                        <div className="rounded-xl border border-gray-100 bg-white px-4 py-3.5 text-center">
                          <p className="text-xs text-gray-500 font-semibold uppercase tracking-[0.14em] mb-1">
                            Pattern
                          </p>
                          <p
                            className={`text-sm font-semibold ${data.categories.responseDelay.details?.isPatternSuspicious
                              ? 'text-rose-600'
                              : 'text-emerald-600'
                              }`}
                          >
                            {data.categories.responseDelay.details?.isPatternSuspicious
                              ? 'Suspicious'
                              : 'Normal'}
                          </p>
                        </div>
                      </div>
                      {data.categories.responseDelay.details?.maxDelayMs != null && (
                        <div className="rounded-xl border border-gray-100 bg-white px-4 py-3 text-xs text-gray-700">
                          <span className="text-gray-500">Max delay:</span>{' '}
                          <span className="font-medium text-gray-900">
                            {Math.round(
                              (data.categories.responseDelay.details.maxDelayMs / 1000) *
                              10
                            ) / 10}
                            s
                          </span>
                          {data.categories.responseDelay.details?.longPauseCount !=
                            null && (
                              <>
                                <span className="mx-2 text-gray-300">•</span>
                                <span className="text-gray-500">≥1 min pauses:</span>{' '}
                                <span
                                  className={`font-semibold ${data.categories.responseDelay.details.longPauseCount > 0
                                    ? 'text-rose-700'
                                    : 'text-gray-900'
                                    }`}
                                >
                                  {
                                    data.categories.responseDelay.details.longPauseCount
                                  }
                                </span>
                              </>
                            )}
                        </div>
                      )}
                      {/* Calculated Criteria — Response Timing */}
                      <div className="group inline-block relative cursor-help mt-4">
                        <span className="text-xs font-semibold text-gray-600 uppercase border-b border-dashed border-gray-500 pb-0.5">
                          Calculated Criteria
                        </span>
                        <div
                          role="tooltip"
                          className="pointer-events-none absolute left-0 bottom-full mb-1 z-20 w-80 max-w-[calc(100vw-2rem)] rounded-xl border border-gray-200 bg-gray-900 px-4 py-3 shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity duration-200"
                        >
                          <div className="absolute left-4 -bottom-1 h-2 w-2 rotate-45 border-r border-b border-gray-200 bg-gray-900" />
                          {(() => {
                            const details =
                              data.categories.responseDelay.details || {};
                            const sampleCount = details.sampleCount ?? 0;
                            const delayIsSuspicious = details.isPatternSuspicious ?? false;
                            const longPauseCount = details.longPauseCount ?? 0;
                            if (!sampleCount) {
                              return (
                                <p className="text-xs text-gray-100 relative">
                                  Response Timing score is 0 because there were no timing samples to analyse.
                                </p>
                              );
                            }
                            let rawDelayScore = 0;
                            if (delayIsSuspicious) rawDelayScore += 10;
                            if (longPauseCount > 0) {
                              rawDelayScore += 40 + Math.min(longPauseCount - 1, 3) * 10;
                            }
                            const delayWeight = 0.5;
                            const weightedScore = rawDelayScore * delayWeight;
                            const finalScore = Math.round(weightedScore);
                            if (!rawDelayScore) {
                              return (
                                <p className="text-xs text-gray-100 relative">
                                  Wait times looked natural (pattern normal and no ≥1 minute pauses), so
                                  Response Timing adds 0 pts.
                                </p>
                              );
                            }
                            return (
                              <div className="space-y-1.5 text-xs text-gray-100 relative">
                                <p className="font-semibold text-gray-50">
                                  How we reached {finalScore} pts:
                                </p>
                                <p>
                                  • Pattern flag:{' '}
                                  <span className="font-semibold">
                                    {delayIsSuspicious ? '+10' : '0'} raw pts
                                  </span>
                                </p>
                                <p>
                                  • Long pauses (≥1 min): {longPauseCount} →{' '}
                                  <span className="font-semibold">
                                    {longPauseCount > 0
                                      ? 40 + Math.min(longPauseCount - 1, 3) * 10
                                      : 0}{' '}
                                    raw pts
                                  </span>
                                </p>
                                <p>
                                  • Raw total: {rawDelayScore} pts × weight 0.5 ={' '}
                                  <span className="font-semibold">
                                    {weightedScore.toFixed(1)} pts
                                  </span>{' '}
                                  → rounded to <span className="font-semibold">{finalScore} pts</span> shown
                                  above.
                                </p>
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                    </div>
                  </div>
                ) : selectedProctoringCategory === 'speechPattern' &&
                  data.categories?.speechPattern ? (
                  /* Speech Patterns detailed card */
                  <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden transform transition-all duration-300 ease-out">
                    <div className="px-5 py-4 border-b border-gray-100 bg-gradient-to-r from-indigo-50/80 via-white to-white flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl bg-indigo-600 flex items-center justify-center shadow-sm">
                          <svg
                            className="w-4.5 h-4.5 text-white"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M5 8.25v7.5M9 6v12m4-9v6m4-8.25v10.5"
                            />
                          </svg>
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-[0.14em]">
                            Behaviour Signal
                          </p>
                          <h2 className="text-base font-bold text-gray-900 leading-tight">
                            Speech Patterns Breakdown
                          </h2>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <p className="text-[11px] text-gray-500 font-semibold uppercase tracking-[0.14em]">
                            Contribution
                          </p>
                          <p className="text-sm font-bold text-gray-900">
                            +{data.categories.speechPattern.score} pts
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="px-5 pt-4 pb-5 bg-gray-50/40 space-y-4">
                      <div className="rounded-xl border border-gray-100 bg-white px-4 py-3.5">
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-[0.14em] mb-1.5">
                          How this works
                        </p>
                        <p className="text-sm text-gray-700">
                          We analyse speaking speed and how much it varies. Very flat speed across the whole
                          {isAssessment ? 'assessment' : 'interview'} can look like someone reading from a script instead of speaking naturally.
                        </p>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div className="rounded-xl border border-gray-100 bg-white px-4 py-3.5 text-center">
                          <p className="text-xs text-gray-500 font-semibold uppercase tracking-[0.14em] mb-1">
                            Samples
                          </p>
                          <p className="text-lg font-semibold text-gray-900">
                            {data.categories.speechPattern.details?.sampleCount ?? 0}
                          </p>
                        </div>
                        <div className="rounded-xl border border-gray-100 bg-white px-4 py-3.5 text-center">
                          <p className="text-xs text-gray-500 font-semibold uppercase tracking-[0.14em] mb-1">
                            Mean WPM
                          </p>
                          <p className="text-lg font-semibold text-gray-900">
                            {data.categories.speechPattern.details?.meanWpm != null
                              ? Math.round(
                                data.categories.speechPattern.details.meanWpm,
                              )
                              : '—'}
                          </p>
                        </div>
                        <div className="rounded-xl border border-gray-100 bg-white px-4 py-3.5 text-center">
                          <p className="text-xs text-gray-500 font-semibold uppercase tracking-[0.14em] mb-1">
                            CV (WPM)
                          </p>
                          <p className="text-lg font-semibold text-gray-900">
                            {data.categories.speechPattern.details?.cvWpm != null
                              ? data.categories.speechPattern.details.cvWpm.toFixed(2)
                              : '—'}
                          </p>
                        </div>
                        <div className="rounded-xl border border-gray-100 bg-white px-4 py-3.5 text-center">
                          <p className="text-xs text-gray-500 font-semibold uppercase tracking-[0.14em] mb-1">
                            Pattern
                          </p>
                          <p
                            className={`text-sm font-semibold ${data.categories.speechPattern.details?.isPatternSuspicious
                              ? 'text-rose-600'
                              : 'text-emerald-600'
                              }`}
                          >
                            {data.categories.speechPattern.details?.isPatternSuspicious
                              ? 'Suspicious'
                              : 'Normal'}
                          </p>
                        </div>
                      </div>
                      {/* Calculated Criteria — Speech Patterns */}
                      <div className="group inline-block relative cursor-help mt-4">
                        <span className="text-xs font-semibold text-gray-600 uppercase border-b border-dashed border-gray-500 pb-0.5">
                          Calculated Criteria
                        </span>
                        <div
                          role="tooltip"
                          className="pointer-events-none absolute left-0 bottom-full mb-1 z-20 w-80 max-w-[calc(100vw-2rem)] rounded-xl border border-gray-200 bg-gray-900 px-4 py-3 shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity duration-200"
                        >
                          <div className="absolute left-4 -bottom-1 h-2 w-2 rotate-45 border-r border-b border-gray-200 bg-gray-900" />
                          {(() => {
                            const details =
                              data.categories.speechPattern.details || {};
                            const sampleCount = details.sampleCount ?? 0;
                            const isPatternSuspicious = details.isPatternSuspicious ?? false;
                            if (!sampleCount) {
                              return (
                                <p className="text-xs text-gray-100 relative">
                                  Speech Patterns score is 0 because there were no speech samples to analyse.
                                </p>
                              );
                            }
                            const rawSpeechScore = isPatternSuspicious ? 12.5 : 0;
                            const speechWeight = 0.5;
                            const weightedScore = rawSpeechScore * speechWeight;
                            const finalScore = Math.round(weightedScore);
                            if (!rawSpeechScore) {
                              return (
                                <p className="text-xs text-gray-100 relative">
                                  Variation in speaking speed looked normal, so Speech Patterns adds 0 pts.
                                </p>
                              );
                            }
                            return (
                              <div className="space-y-1.5 text-xs text-gray-100 relative">
                                <p className="font-semibold text-gray-50">
                                  How we reached {finalScore} pts:
                                </p>
                                <p>
                                  • Pattern flag:{' '}
                                  <span className="font-semibold">
                                    {isPatternSuspicious ? '12.5' : '0'} raw pts
                                  </span>
                                </p>
                                <p>
                                  • Raw total × weight: {rawSpeechScore} × 0.5 ={' '}
                                  <span className="font-semibold">
                                    {weightedScore.toFixed(1)} pts
                                  </span>{' '}
                                  → rounded to <span className="font-semibold">{finalScore} pts</span> shown
                                  above.
                                </p>
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                    </div>
                  </div>
                ) : selectedProctoringCategory === 'videoPresence' &&
                  data.categories?.videoPresence ? (
                  /* Video Presence detailed card */
                  <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden transform transition-all duration-300 ease-out">
                    <div className="px-5 py-4 border-b border-gray-100 bg-gradient-to-r from-indigo-50/80 via-white to-white flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl bg-indigo-600 flex items-center justify-center shadow-sm">
                          <svg
                            className="w-4.5 h-4.5 text-white"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M15.75 10.5V6A2.25 2.25 0 0013.5 3.75h-9A2.25 2.25 0 002.25 6v8.25A2.25 2.25 0 004.5 16.5h9a2.25 2.25 0 002.25-2.25v-1.128l3.22 1.932A.75.75 0 0021 13.5v-3a.75.75 0 00-.38-.654L15.75 8.25"
                            />
                          </svg>
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-[0.14em]">
                            Video Signal
                          </p>
                          <h2 className="text-base font-bold text-gray-900 leading-tight">Video Presence</h2>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <p className="text-[11px] text-gray-500 font-semibold uppercase tracking-[0.14em]">
                            Contribution
                          </p>
                          <p className="text-sm font-bold text-gray-900">
                            +{data.categories.videoPresence.score} pts
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="px-5 pt-4 pb-5 bg-gray-50/40 space-y-4">
                      <div className="rounded-xl border border-gray-100 bg-white px-4 py-3.5">
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-[0.14em] mb-1.5">
                          How this works
                        </p>
                        <p className="text-sm text-gray-700">
                          We track how often and how long the candidate disappears from camera. More time
                          off-screen means more risk points.
                        </p>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-2">
                        <div className="rounded-xl border border-gray-100 bg-white px-4 py-3.5 text-center">
                          <p className="text-xs text-gray-500 font-semibold uppercase tracking-[0.14em] mb-1">
                            Not Visible Events
                          </p>
                          <p
                            className={`text-lg font-semibold ${data.categories.videoPresence.details
                              ?.faceNotVisibleEvents > 0
                              ? 'text-rose-600'
                              : 'text-gray-900'
                              }`}
                          >
                            {data.categories.videoPresence.details
                              ?.faceNotVisibleEvents ?? 0}
                          </p>
                        </div>
                        <div className="rounded-xl border border-gray-100 bg-white px-4 py-3.5 text-center">
                          <p className="text-xs text-gray-500 font-semibold uppercase tracking-[0.14em] mb-1">
                            Total Duration (s)
                          </p>
                          <p
                            className={`text-lg font-semibold ${data.categories.videoPresence.details
                              ?.faceNotVisibleTotalSeconds > 0
                              ? 'text-rose-600'
                              : 'text-gray-900'
                              }`}
                          >
                            {data.categories.videoPresence.details
                              ?.faceNotVisibleTotalSeconds ?? 0}
                            s
                          </p>
                        </div>
                      </div>
                      {/* Calculated Criteria — Video Presence */}
                      <div className="group inline-block relative cursor-help mt-4">
                        <span className="text-xs font-semibold text-gray-600 uppercase border-b border-dashed border-gray-500 pb-0.5">
                          Calculated Criteria
                        </span>
                        <div
                          role="tooltip"
                          className="pointer-events-none absolute left-0 bottom-full mb-1 z-20 w-80 max-w-[calc(100vw-2rem)] rounded-xl border border-gray-200 bg-gray-900 px-4 py-3 shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity duration-200"
                        >
                          <div className="absolute left-4 -bottom-1 h-2 w-2 rotate-45 border-r border-b border-gray-200 bg-gray-900" />
                          {(() => {
                            const details =
                              data.categories.videoPresence.details || {};
                            const faceNotVisibleEvents = details.faceNotVisibleEvents ?? 0;
                            const totalSeconds = details.faceNotVisibleTotalSeconds ?? 0;
                            if (!faceNotVisibleEvents && !totalSeconds) {
                              return (
                                <p className="text-xs text-gray-100 relative">
                                  The candidate stayed on camera the whole time, so Video Presence adds 0 pts.
                                </p>
                              );
                            }
                            const basePresencePts = faceNotVisibleEvents * 5;
                            const extraPresencePts =
                              Math.min(Math.floor(totalSeconds / 10), 6) * 1.5;
                            const rawPresenceScore = basePresencePts + extraPresencePts;
                            const presenceWeight = 0.8;
                            const weightedScore = rawPresenceScore * presenceWeight;
                            const finalScore = Math.round(weightedScore);
                            return (
                              <div className="space-y-1.5 text-xs text-gray-100 relative">
                                <p className="font-semibold text-gray-50">
                                  How we reached {finalScore} pts:
                                </p>
                                <p>
                                  • Off‑camera events: {faceNotVisibleEvents} × 5 ={' '}
                                  <span className="font-semibold">
                                    {basePresencePts} raw pts
                                  </span>
                                </p>
                                <p>
                                  • Extra time off‑screen: floor({totalSeconds} ÷ 10) capped at 6 →{' '}
                                  <span className="font-semibold">
                                    {Math.min(Math.floor(totalSeconds / 10), 6)} × 1.5 = {extraPresencePts} raw
                                    pts
                                  </span>
                                </p>
                                <p>
                                  • Raw total × weight: {rawPresenceScore} × 0.8 ={' '}
                                  <span className="font-semibold">
                                    {weightedScore.toFixed(1)} pts
                                  </span>{' '}
                                  → rounded to <span className="font-semibold">{finalScore} pts</span> shown
                                  above.
                                </p>
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                    </div>
                  </div>
                ) : selectedProctoringCategory === 'videoMultipleFaces' &&
                  data.categories?.videoMultipleFaces ? (
                  /* Multiple Faces Detected detailed card */
                  <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden transform transition-all duration-300 ease-out">
                    <div className="px-5 py-4 border-b border-gray-100 bg-gradient-to-r from-indigo-50/80 via-white to-white flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl bg-indigo-600 flex items-center justify-center shadow-sm">
                          <svg
                            className="w-4.5 h-4.5 text-white"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M9.75 6.75a3 3 0 11-6 0 3 3 0 016 0zm10.5 0a3 3 0 11-6 0 3 3 0 016 0zM4.5 15.75a4.5 4.5 0 019 0v.75H4.5v-.75zm9.75 0a4.5 4.5 0 018.25 2.25v.75H14.25v-.75a4.5 4.5 0 010-2.25z"
                            />
                          </svg>
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-[0.14em]">
                            Video Signal
                          </p>
                          <h2 className="text-base font-bold text-gray-900 leading-tight">
                            Multiple Faces Detected
                          </h2>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <p className="text-[11px] text-gray-500 font-semibold uppercase tracking-[0.14em]">
                            Contribution
                          </p>
                          <p className="text-sm font-bold text-gray-900">
                            +{data.categories.videoMultipleFaces.score} pts
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="px-5 pt-4 pb-5 bg-gray-50/40 space-y-4">
                      <div className="rounded-xl border border-gray-100 bg-white px-4 py-3.5">
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-[0.14em] mb-1.5">
                          How this works
                        </p>
                        <p className="text-sm text-gray-700">
                          We look for more than one face in the frame. Every time another person shows up, it
                          increases the risk score.
                        </p>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-2">
                        <div className="rounded-xl border border-gray-100 bg-white px-4 py-3.5 text-center">
                          <p className="text-xs text-gray-500 font-semibold uppercase tracking-[0.14em] mb-1">
                            Multiple Faces Events
                          </p>
                          <p
                            className={`text-lg font-semibold ${data.categories.videoMultipleFaces.details?.multiFaceEvents >
                              0
                              ? 'text-rose-600'
                              : 'text-gray-900'
                              }`}
                          >
                            {data.categories.videoMultipleFaces.details?.multiFaceEvents ??
                              0}
                          </p>
                        </div>
                      </div>
                      {/* Calculated Criteria — Multiple Faces Detected */}
                      <div className="group inline-block relative cursor-help mt-4">
                        <span className="text-xs font-semibold text-gray-600 uppercase border-b border-dashed border-gray-500 pb-0.5">
                          Calculated Criteria
                        </span>
                        <div
                          role="tooltip"
                          className="pointer-events-none absolute left-0 bottom-full mb-1 z-20 w-80 max-w-[calc(100vw-2rem)] rounded-xl border border-gray-200 bg-gray-900 px-4 py-3 shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity duration-200"
                        >
                          <div className="absolute left-4 -bottom-1 h-2 w-2 rotate-45 border-r border-b border-gray-200 bg-gray-900" />
                          {(() => {
                            const details =
                              data.categories.videoMultipleFaces.details || {};
                            const multiFaceEvents = details.multiFaceEvents ?? 0;
                            if (!multiFaceEvents) {
                              return (
                                <p className="text-xs text-gray-100 relative">
                                  No extra faces were detected in the frame, so Multiple Faces Detected adds 0
                                  pts.
                                </p>
                              );
                            }
                            const eventsCapped = Math.min(multiFaceEvents, 3);
                            const rawMultiFaceScore = eventsCapped * 10;
                            const multiFaceWeight = 1.0;
                            const weightedScore = rawMultiFaceScore * multiFaceWeight;
                            const finalScore = Math.round(weightedScore);
                            return (
                              <div className="space-y-1.5 text-xs text-gray-100 relative">
                                <p className="font-semibold text-gray-50">
                                  How we reached {finalScore} pts:
                                </p>
                                <p>
                                  • Multiple‑face events: {multiFaceEvents} (capped at 3) → {eventsCapped} × 10
                                  = <span className="font-semibold">{rawMultiFaceScore} raw pts</span>
                                </p>
                                <p>
                                  • Raw total × weight: {rawMultiFaceScore} × 1.0 ={' '}
                                  <span className="font-semibold">
                                    {weightedScore.toFixed(1)} pts
                                  </span>{' '}
                                  → rounded to <span className="font-semibold">{finalScore} pts</span> shown
                                  above.
                                </p>
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                      {data.categories.videoMultipleFaces.details?.evidence?.length >
                        0 && (
                          <div className="rounded-xl border border-gray-100 bg-white px-4 py-3.5 mt-2">
                            <p className="text-xs font-semibold text-gray-700 uppercase mb-2 tracking-[0.12em]">
                              Evidence Frames
                            </p>
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                              {data.categories.videoMultipleFaces.details.evidence.map(
                                (url, i) => (
                                  <a
                                    key={i}
                                    href={url}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="block relative aspect-video bg-gray-100 rounded-lg overflow-hidden border border-gray-200 shadow-sm hover:opacity-90 transition-opacity"
                                  >
                                    <img
                                      src={url}
                                      alt="Evidence"
                                      className="absolute inset-0 w-full h-full object-cover"
                                    />
                                  </a>
                                ),
                              )}
                            </div>
                          </div>
                        )}
                    </div>
                  </div>
                ) : selectedProctoringCategory === 'videoEyeGaze' &&
                  data.categories?.videoEyeGaze ? (
                  /* Eye Gaze detailed card */
                  <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden transform transition-all duration-300 ease-out">
                    <div className="px-5 py-4 border-b border-gray-100 bg-gradient-to-r from-indigo-50/80 via-white to-white flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl bg-indigo-600 flex items-center justify-center shadow-sm">
                          <svg
                            className="w-4.5 h-4.5 text-white"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M2.25 12s3.75-6.75 9.75-6.75S21.75 12 21.75 12 18 18.75 12 18.75 2.25 12 2.25 12zm9.75 3a3 3 0 100-6 3 3 0 000 6z"
                            />
                          </svg>
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-[0.14em]">
                            Video Signal
                          </p>
                          <h2 className="text-base font-bold text-gray-900 leading-tight">Eye Gaze</h2>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <p className="text-[11px] text-gray-500 font-semibold uppercase tracking-[0.14em]">
                            Contribution
                          </p>
                          <p className="text-sm font-bold text-gray-900">
                            +{data.categories.videoEyeGaze.score} pts
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="px-5 pt-4 pb-5 bg-gray-50/40 space-y-4">
                      <div className="rounded-xl border border-gray-100 bg-white px-4 py-3.5">
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-[0.14em] mb-1.5">
                          How this works
                        </p>
                        <p className="text-sm text-gray-700">
                          We track how often the candidate’s eyes clearly move away from the screen or camera
                          for noticeable chunks of time. More long looks away add more points.
                        </p>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-2">
                        <div className="rounded-xl border border-gray-100 bg-white px-4 py-3.5 text-center">
                          <p className="text-xs text-gray-500 font-semibold uppercase tracking-[0.14em] mb-1">
                            Looking Away Events
                          </p>
                          <p
                            className={`text-lg font-semibold ${data.categories.videoEyeGaze.details?.eyeGazeAwayEvents > 0
                              ? 'text-orange-600'
                              : 'text-gray-900'
                              }`}
                          >
                            {data.categories.videoEyeGaze.details
                              ?.eyeGazeAwayEvents ?? 0}
                          </p>
                        </div>
                      </div>
                      {/* Calculated Criteria — Eye Gaze */}
                      <div className="group inline-block relative cursor-help mt-4">
                        <span className="text-xs font-semibold text-gray-600 uppercase border-b border-dashed border-gray-500 pb-0.5">
                          Calculated Criteria
                        </span>
                        <div
                          role="tooltip"
                          className="pointer-events-none absolute left-0 bottom-full mb-1 z-20 w-80 max-w-[calc(100vw-2rem)] rounded-xl border border-gray-200 bg-gray-900 px-4 py-3 shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity duration-200"
                        >
                          <div className="absolute left-4 -bottom-1 h-2 w-2 rotate-45 border-r border-b border-gray-200 bg-gray-900" />
                          {(() => {
                            const details =
                              data.categories.videoEyeGaze.details || {};
                            const eyeGazeAwayEvents = details.eyeGazeAwayEvents ?? 0;
                            if (!eyeGazeAwayEvents) {
                              return (
                                <p className="text-xs text-gray-100 relative">
                                  Clear eye contact was maintained (no long looks away), so Eye Gaze adds 0
                                  pts.
                                </p>
                              );
                            }
                            const cat = data.categories.videoEyeGaze;
                            const { eventCap, rawPtsPerEvent, weight: defaultWeight } =
                              PROCTORING_EYE_GAZE_CRITERIA;
                            const eventsCapped = Math.min(eyeGazeAwayEvents, eventCap);
                            const rawEyeGazeScore =
                              typeof cat?.rawScore === 'number'
                                ? cat.rawScore
                                : eventsCapped * rawPtsPerEvent;
                            const eyeGazeWeight =
                              typeof cat?.weight === 'number' ? cat.weight : defaultWeight;
                            const weightedScore = rawEyeGazeScore * eyeGazeWeight;
                            const finalScore =
                              typeof cat?.score === 'number'
                                ? cat.score
                                : Math.round(weightedScore);
                            return (
                              <div className="space-y-1.5 text-xs text-gray-100 relative">
                                <p className="font-semibold text-gray-50">
                                  How we reached {finalScore} pts:
                                </p>
                                <p>
                                  • Looking‑away events: {eyeGazeAwayEvents} (capped at {eventCap}) →{' '}
                                  {eventsCapped} × {rawPtsPerEvent} ={' '}
                                  <span className="font-semibold">{rawEyeGazeScore} raw pts</span>
                                </p>
                                <p>
                                  • Raw total × weight: {rawEyeGazeScore} × {eyeGazeWeight} ={' '}
                                  <span className="font-semibold">
                                    {weightedScore.toFixed(1)} pts
                                  </span>{' '}
                                  → rounded to <span className="font-semibold">{finalScore} pts</span> shown
                                  above.
                                </p>
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                      {data.categories.videoEyeGaze.details?.evidence?.length > 0 && (
                        <div className="rounded-xl border border-gray-100 bg-white px-4 py-3.5 mt-2">
                          <p className="text-xs font-semibold text-gray-700 uppercase mb-2 tracking-[0.12em]">
                            Evidence Frames
                          </p>
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                            {data.categories.videoEyeGaze.details.evidence.map(
                              (url, i) => (
                                <a
                                  key={i}
                                  href={url}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="block relative aspect-video bg-gray-100 rounded-lg overflow-hidden border border-gray-200 shadow-sm hover:opacity-90 transition-opacity"
                                >
                                  <img
                                    src={url}
                                    alt="Evidence"
                                    className="absolute inset-0 w-full h-full object-cover"
                                  />
                                </a>
                              ),
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ) : selectedProctoringCategory === 'videoHeadPose' &&
                  data.categories?.videoHeadPose ? (
                  /* Head Pose detailed card */
                  <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden transform transition-all duration-300 ease-out">
                    <div className="px-5 py-4 border-b border-gray-100 bg-gradient-to-r from-indigo-50/80 via-white to-white flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl bg-indigo-600 flex items-center justify-center shadow-sm">
                          <svg
                            className="w-4.5 h-4.5 text-white"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M12 4.5c-2.485 0-4.5 2.015-4.5 4.5S9.515 13.5 12 13.5 16.5 11.485 16.5 9 14.485 4.5 12 4.5zM6.75 18.75A5.25 5.25 0 0112 15h0a5.25 5.25 0 015.25 3.75"
                            />
                          </svg>
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-[0.14em]">
                            Video Signal
                          </p>
                          <h2 className="text-base font-bold text-gray-900 leading-tight">Head Pose</h2>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <p className="text-[11px] text-gray-500 font-semibold uppercase tracking-[0.14em]">
                            Contribution
                          </p>
                          <p className="text-sm font-bold text-gray-900">
                            +{data.categories.videoHeadPose.score} pts
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="px-5 pt-4 pb-5 bg-gray-50/40 space-y-4">
                      <div className="rounded-xl border border-gray-100 bg-white px-4 py-3.5">
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-[0.14em] mb-1.5">
                          How this works
                        </p>
                        <p className="text-sm text-gray-700">
                          We watch for moments where the candidate’s head is turned far away from the screen,
                          like reading from something off to the side. More strong turns away increase the
                          risk.
                        </p>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-2">
                        <div className="rounded-xl border border-gray-100 bg-white px-4 py-3.5 text-center">
                          <p className="text-xs text-gray-500 font-semibold uppercase tracking-[0.14em] mb-1">
                            Head Turned Away Events
                          </p>
                          <p
                            className={`text-lg font-semibold ${data.categories.videoHeadPose.details
                              ?.headPoseAwayEvents > 0
                              ? 'text-rose-600'
                              : 'text-gray-900'
                              }`}
                          >
                            {data.categories.videoHeadPose.details
                              ?.headPoseAwayEvents ?? 0}
                          </p>
                        </div>
                      </div>
                      {/* Calculated Criteria — Head Pose */}
                      <div className="group inline-block relative cursor-help mt-4">
                        <span className="text-xs font-semibold text-gray-600 uppercase border-b border-dashed border-gray-500 pb-0.5">
                          Calculated Criteria
                        </span>
                        <div
                          role="tooltip"
                          className="pointer-events-none absolute left-0 bottom-full mb-1 z-20 w-80 max-w-[calc(100vw-2rem)] rounded-xl border border-gray-200 bg-gray-900 px-4 py-3 shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity duration-200"
                        >
                          <div className="absolute left-4 -bottom-1 h-2 w-2 rotate-45 border-r border-b border-gray-200 bg-gray-900" />
                          {(() => {
                            const details =
                              data.categories.videoHeadPose.details || {};
                            const headPoseAwayEvents = details.headPoseAwayEvents ?? 0;
                            if (!headPoseAwayEvents) {
                              return (
                                <p className="text-xs text-gray-100 relative">
                                  The candidate did not clearly turn their head away from the screen, so Head
                                  Pose adds 0 pts.
                                </p>
                              );
                            }
                            const cat = data.categories.videoHeadPose;
                            const eventsCapped = Math.min(headPoseAwayEvents, 5);
                            const rawHeadPoseScore =
                              typeof cat?.rawScore === 'number'
                                ? cat.rawScore
                                : eventsCapped * 4;
                            const headPoseWeight =
                              typeof cat?.weight === 'number' ? cat.weight : 1.0;
                            const weightedScore = rawHeadPoseScore * headPoseWeight;
                            const finalScore =
                              typeof cat?.score === 'number'
                                ? cat.score
                                : Math.round(weightedScore);
                            return (
                              <div className="space-y-1.5 text-xs text-gray-100 relative">
                                <p className="font-semibold text-gray-50">
                                  How we reached {finalScore} pts:
                                </p>
                                <p>
                                  • Head‑turned‑away events: {headPoseAwayEvents} (capped at 5) → {eventsCapped}
                                  × 4 = <span className="font-semibold">{rawHeadPoseScore} raw pts</span>
                                </p>
                                <p>
                                  • Raw total × weight: {rawHeadPoseScore} × {headPoseWeight} ={' '}
                                  <span className="font-semibold">
                                    {weightedScore.toFixed(1)} pts
                                  </span>{' '}
                                  → rounded to <span className="font-semibold">{finalScore} pts</span> shown
                                  above.
                                </p>
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                      {data.categories.videoHeadPose.details?.evidence?.length > 0 && (
                        <div className="rounded-xl border border-gray-100 bg-white px-4 py-3.5 mt-2">
                          <p className="text-xs font-semibold text-gray-700 uppercase mb-2 tracking-[0.12em]">
                            Evidence Frames
                          </p>
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                            {data.categories.videoHeadPose.details.evidence.map(
                              (url, i) => (
                                <a
                                  key={i}
                                  href={url}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="block relative aspect-video bg-gray-100 rounded-lg overflow-hidden border border-gray-200 shadow-sm hover:opacity-90 transition-opacity"
                                >
                                  <img
                                    src={url}
                                    alt="Evidence"
                                    className="absolute inset-0 w-full h-full object-cover"
                                  />
                                </a>
                              ),
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ) : selectedProctoringCategory === 'videoPhoneDetection' &&
                  data.categories?.videoPhoneDetection ? (
                  /* Phone Detection detailed card */
                  <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden transform transition-all duration-300 ease-out">
                    <div className="px-5 py-4 border-b border-gray-100 bg-gradient-to-r from-indigo-50/80 via-white to-white flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl bg-indigo-600 flex items-center justify-center shadow-sm">
                          <svg
                            className="w-4.5 h-4.5 text-white"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M7.5 3.75h9A1.75 1.75 0 0118.25 5.5v13A1.75 1.75 0 0116.5 20.25h-9A1.75 1.75 0 015.75 18.5v-13A1.75 1.75 0 017.5 3.75zm4.5 12.5h.01"
                            />
                          </svg>
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-[0.14em]">
                            Video Signal
                          </p>
                          <h2 className="text-base font-bold text-gray-900 leading-tight">Phone Detected</h2>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <p className="text-[11px] text-gray-500 font-semibold uppercase tracking-[0.14em]">
                            Contribution
                          </p>
                          <p className="text-sm font-bold text-gray-900">
                            +{data.categories.videoPhoneDetection.score} pts
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="px-5 pt-4 pb-5 bg-gray-50/40 space-y-4">
                      <div className="rounded-xl border border-gray-100 bg-white px-4 py-3.5">
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-[0.14em] mb-1.5">
                          How this works
                        </p>
                        <p className="text-sm text-gray-700">
                          We flag moments where a phone or similar device is clearly visible in the frame. Each
                          clear sighting is treated as a serious incident.
                        </p>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-2">
                        <div className="rounded-xl border border-gray-100 bg-white px-4 py-3.5 text-center">
                          <p className="text-xs text-gray-500 font-semibold uppercase tracking-[0.14em] mb-1">
                            Critical Incidents
                          </p>
                          <p className="text-lg font-bold text-rose-700">
                            {data.categories.videoPhoneDetection.details
                              ?.phoneDetectedEvents ?? 0}
                          </p>
                        </div>
                      </div>
                      {/* Calculated Criteria — Phone Detected */}
                      <div className="group inline-block relative cursor-help mt-4">
                        <span className="text-xs font-semibold text-gray-600 uppercase border-b border-dashed border-gray-500 pb-0.5">
                          Calculated Criteria
                        </span>
                        <div
                          role="tooltip"
                          className="pointer-events-none absolute left-0 bottom-full mb-1 z-20 w-80 max-w-[calc(100vw-2rem)] rounded-xl border border-gray-200 bg-gray-900 px-4 py-3 shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity duration-200"
                        >
                          <div className="absolute left-4 -bottom-1 h-2 w-2 rotate-45 border-r border-b border-gray-200 bg-gray-900" />
                          {(() => {
                            const details =
                              data.categories.videoPhoneDetection.details || {};
                            const phoneDetectedEvents = details.phoneDetectedEvents ?? 0;
                            if (!phoneDetectedEvents) {
                              return (
                                <p className="text-xs text-gray-100 relative">
                                  No phones were detected on camera, so Phone Detected adds 0 pts.
                                </p>
                              );
                            }
                            const cat = data.categories.videoPhoneDetection;
                            const extraEvents = Math.min(phoneDetectedEvents - 1, 3);
                            const rawPhoneScore =
                              typeof cat?.rawScore === 'number'
                                ? cat.rawScore
                                : 20 + extraEvents * 7.5;
                            const phoneWeight = typeof cat?.weight === 'number' ? cat.weight : 1.0;
                            const weightedScore = rawPhoneScore * phoneWeight;
                            const finalScore =
                              typeof cat?.score === 'number'
                                ? cat.score
                                : Math.round(weightedScore);
                            return (
                              <div className="space-y-1.5 text-xs text-gray-100 relative">
                                <p className="font-semibold text-gray-50">
                                  How we reached {finalScore} pts:
                                </p>
                                <p>
                                  • First phone detection:{' '}
                                  <span className="font-semibold">20 raw pts</span>
                                </p>
                                <p>
                                  • Extra detections (capped at 3): {phoneDetectedEvents - 1} → min(
                                  {phoneDetectedEvents - 1}, 3) × 7.5 ={' '}
                                  <span className="font-semibold">{extraEvents * 7.5} raw pts</span>
                                </p>
                                <p>
                                  • Raw total × weight: {rawPhoneScore} × {phoneWeight} ={' '}
                                  <span className="font-semibold">
                                    {weightedScore.toFixed(1)} pts
                                  </span>{' '}
                                  → rounded to <span className="font-semibold">{finalScore} pts</span> shown
                                  above.
                                </p>
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                      {data.categories.videoPhoneDetection.details?.evidence?.length >
                        0 && (
                          <div className="rounded-xl border border-gray-100 bg-white px-4 py-3.5 mt-2">
                            <p className="text-xs font-semibold text-gray-700 uppercase mb-2 tracking-[0.12em]">
                              Evidence Frames
                            </p>
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                              {data.categories.videoPhoneDetection.details.evidence.map(
                                (url, i) => (
                                  <a
                                    key={i}
                                    href={url}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="block relative aspect-video bg-gray-100 rounded-lg overflow-hidden border border-gray-200 shadow-sm hover:opacity-90 transition-opacity"
                                  >
                                    <img
                                      src={url}
                                      alt="Evidence"
                                      className="absolute inset-0 w-full h-full object-cover"
                                    />
                                  </a>
                                ),
                              )}
                            </div>
                          </div>
                        )}
                    </div>
                  </div>
                ) : selectedProctoringCategory === 'videoIdentity' &&
                  data.categories?.videoIdentity ? (
                  /* Face Identity detailed card */
                  <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden transform transition-all duration-300 ease-out">
                    <div className="px-5 py-4 border-b border-gray-100 bg-gradient-to-r from-indigo-50/80 via-white to-white flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl bg-indigo-600 flex items-center justify-center shadow-sm">
                          <svg
                            className="w-4.5 h-4.5 text-white"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 1.5c-3.038 0-5.5 2.015-5.5 4.5v1a.75.75 0 00.75.75h9.5a.75.75 0 00.75-.75v-1c0-2.485-2.462-4.5-5.5-4.5z"
                            />
                          </svg>
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-[0.14em]">
                            Video Signal
                          </p>
                          <h2 className="text-base font-bold text-gray-900 leading-tight">Face Identity</h2>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <p className="text-[11px] text-gray-500 font-semibold uppercase tracking-[0.14em]">
                            Contribution
                          </p>
                          <p className="text-sm font-bold text-gray-900">
                            +{data.categories.videoIdentity.score} pts
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="px-5 pt-4 pb-5 bg-gray-50/40 space-y-4">
                      <div className="rounded-xl border border-gray-100 bg-white px-4 py-3.5">
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-[0.14em] mb-1.5">
                          How this works
                        </p>
                        <p className="text-sm text-gray-700">
                          We compare every frame to the first frame captured at the start of the test.
                          If the original person is missing or a different person appears, the system flags it.
                        </p>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-2">
                        <div className="rounded-xl border border-gray-100 bg-white px-4 py-3.5 text-center">
                          <p className="text-xs text-gray-500 font-semibold uppercase tracking-[0.14em] mb-1">
                            Identity Mismatches
                          </p>
                          <p
                            className={`text-lg font-semibold ${data.categories.videoIdentity.details
                              ?.identityMismatchEvents > 0
                              ? 'text-rose-600'
                              : 'text-gray-900'
                              }`}
                          >
                            {data.categories.videoIdentity.details
                              ?.identityMismatchEvents ?? 0}
                          </p>
                        </div>
                        <div className="rounded-xl border border-gray-100 bg-white px-4 py-3.5 text-center">
                          <p className="text-xs text-gray-500 font-semibold uppercase tracking-[0.14em] mb-1">
                            Face Not Visible
                          </p>
                          <p
                            className={`text-lg font-semibold ${(data.categories.videoIdentity.details
                              ?.faceNotVisibleEvents || 0) > 0
                              ? 'text-amber-600'
                              : 'text-gray-900'
                              }`}
                          >
                            {data.categories.videoIdentity.details
                              ?.faceNotVisibleEvents ?? 0}
                          </p>
                        </div>
                      </div>
                      {/* Evidence Frames */}
                      {data.categories.videoIdentity.details?.evidence?.length > 0 && (
                        <div className="space-y-2">
                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-[0.14em]">
                            Evidence Frames
                          </p>
                          <div className="flex gap-3 flex-wrap">
                            {data.categories.videoIdentity.details.evidence.map(
                              (imgSrc, idx) => (
                                <div
                                  key={idx}
                                  className="rounded-xl border border-gray-200 overflow-hidden shadow-sm"
                                >
                                  <img
                                    src={imgSrc}
                                    alt={`Identity mismatch evidence ${idx + 1}`}
                                    className="w-36 h-auto object-cover"
                                    loading="lazy"
                                  />
                                </div>
                              ),
                            )}
                          </div>
                        </div>
                      )}
                      {/* Calculated Criteria — Face Identity */}
                      <div className="group inline-block relative cursor-help mt-4">
                        <span className="text-xs font-semibold text-gray-600 uppercase border-b border-dashed border-gray-500 pb-0.5">
                          Calculated Criteria
                        </span>
                        <div
                          role="tooltip"
                          className="pointer-events-none absolute left-0 bottom-full mb-1 z-20 w-80 max-w-[calc(100vw-2rem)] rounded-xl border border-gray-200 bg-gray-900 px-4 py-3 shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity duration-200"
                        >
                          <div className="absolute left-4 -bottom-1 h-2 w-2 rotate-45 border-r border-b border-gray-200 bg-gray-900" />
                          {(() => {
                            const details =
                              data.categories.videoIdentity.details || {};
                            const totalIssues = details.totalIdentityIssues ?? ((details.identityMismatchEvents ?? 0) + (details.faceNotVisibleEvents ?? 0));
                            if (!totalIssues) {
                              return (
                                <p className="text-xs text-gray-100 relative">
                                  The candidate remained on camera throughout and matched the reference identity. Score: 0 pts.
                                </p>
                              );
                            }
                            const cat = data.categories.videoIdentity;
                            return (
                              <div className="space-y-1.5 text-xs text-gray-100 relative">
                                <p className="font-semibold text-gray-50">
                                  How we reached {cat.score} pts:
                                </p>
                                <p>
                                  • Identity mismatches: <span className="font-semibold">{details.identityMismatchEvents ?? 0}</span> events
                                </p>
                                <p>
                                  • Face not visible: <span className="font-semibold">{details.faceNotVisibleEvents ?? 0}</span> events
                                </p>
                                <p>
                                  • Combined issues (capped at 5): min({totalIssues}, 5) × 8 ={' '}
                                  <span className="font-semibold">{Math.min(totalIssues, 5) * 8} pts</span>
                                </p>
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                    </div>
                  </div>
                ) : selectedProctoringCategory === 'speakerDetection' &&
                  data.categories?.speakerDetection ? (
                  /* Multiple Speaker Detection detailed card */
                  <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden transform transition-all duration-300 ease-out">
                    <div className="px-5 py-4 border-b border-gray-100 bg-gradient-to-r from-indigo-50/80 via-white to-white flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl bg-indigo-600 flex items-center justify-center shadow-sm">
                          <svg
                            className="w-4.5 h-4.5 text-white"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-9A2.25 2.25 0 002.25 5.25v9A2.25 2.25 0 004.5 16.5H9M9 12.75L11.25 15 15 9.75M18 9.75v8.25M21 12.75v5.25"
                            />
                          </svg>
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-[0.14em]">
                            Audio Signal
                          </p>
                          <h2 className="text-base font-bold text-gray-900 leading-tight">
                            Multiple Speaker Detection
                          </h2>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <p className="text-[11px] text-gray-500 font-semibold uppercase tracking-[0.14em]">
                            Contribution
                          </p>
                          <p className="text-sm font-bold text-gray-900">
                            +{data.categories.speakerDetection.score} pts
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="px-5 pt-4 pb-5 bg-gray-50/40 space-y-4">
                      <div className="rounded-xl border border-gray-100 bg-white px-4 py-3.5">
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-[0.14em] mb-1.5">
                          How this works
                        </p>
                        <p className="text-sm text-gray-700">
                          We listen for more than one distinct voice while the candidate is answering. More
                          extra voices and more questions affected mean a higher integrity risk.
                        </p>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-2">
                        <div className="rounded-xl border border-gray-100 bg-white px-4 py-3.5 text-center">
                          <p className="text-xs text-gray-500 font-semibold uppercase tracking-[0.14em] mb-1">
                            Number of Speakers
                          </p>
                          <p className="text-lg font-semibold text-gray-900">
                            {data.categories.speakerDetection.details?.uniqueSpeakers ??
                              1}
                          </p>
                        </div>
                        <div className="rounded-xl border border-gray-100 bg-white px-4 py-3.5 text-center">
                          <p className="text-xs text-gray-500 font-semibold uppercase tracking-[0.14em] mb-1">
                            Interventions
                          </p>
                          <p className="text-lg font-semibold text-rose-600">
                            {data.categories.speakerDetection.details?.totalEvents ?? 0}
                          </p>
                        </div>
                        <div className="rounded-xl border border-gray-100 bg-white px-4 py-3.5 text-center">
                          <p className="text-xs text-gray-500 font-semibold uppercase tracking-[0.14em] mb-1">
                            Pattern
                          </p>
                          <p
                            className={`text-sm font-semibold ${data.categories.speakerDetection.details
                              ?.isPatternSuspicious
                              ? 'text-rose-600'
                              : 'text-emerald-600'
                              }`}
                          >
                            {data.categories.speakerDetection.details
                              ?.isPatternSuspicious
                              ? 'Suspicious'
                              : 'Normal'}
                          </p>
                        </div>
                      </div>
                      {data.categories.speakerDetection.events?.length > 0 && (
                        <div className="rounded-xl border border-gray-100 bg-white px-4 py-3.5 space-y-2">
                          <p className="text-xs font-semibold text-gray-700 uppercase tracking-[0.12em]">
                            Detection Events
                          </p>
                          {data.categories.speakerDetection.events.map((evt, idx) => (
                            <div
                              key={idx}
                              className="p-2 bg-rose-50 border border-rose-200 rounded-lg text-sm flex items-start gap-1.5"
                            >
                              <span className="font-medium text-gray-900">
                                Question {evt.questionIndex + 1}:
                              </span>
                              <span className="text-gray-700">
                                {evt.speakerCount} speakers detected
                                {evt.speakerLabels?.length > 0 &&
                                  ` (${evt.speakerLabels.join(', ')})`}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                      {/* Calculated Criteria — Multiple Speaker Detection */}
                      <div className="group inline-block relative cursor-help mt-4">
                        <span className="text-xs font-semibold text-gray-600 uppercase border-b border-dashed border-gray-500 pb-0.5">
                          Calculated Criteria
                        </span>
                        <div
                          role="tooltip"
                          className="pointer-events-none absolute left-0 bottom-full mb-1 z-20 w-80 max-w-[calc(100vw-2rem)] rounded-xl border border-gray-200 bg-gray-900 px-4 py-3 shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity duration-200"
                        >
                          <div className="absolute left-4 -bottom-1 h-2 w-2 rotate-45 border-r border-b border-gray-200 bg-gray-900" />
                          {(() => {
                            const details =
                              data.categories.speakerDetection.details || {};
                            const events =
                              data.categories.speakerDetection.events || [];
                            const questionsWithMultipleSpeakers =
                              details.questionsWithMultipleSpeakers ??
                              new Set(events.map(e => e.questionIndex)).size;
                            const cat = data.categories.speakerDetection;
                            const questionsCapped = Math.min(questionsWithMultipleSpeakers, 3);
                            const rawSpeakerScore =
                              typeof cat?.rawScore === 'number'
                                ? cat.rawScore
                                : questionsCapped * 7.5;
                            const speakerWeight =
                              typeof cat?.weight === 'number' ? cat.weight : 0.6;
                            const weightedScore = rawSpeakerScore * speakerWeight;
                            const finalScore =
                              typeof cat?.score === 'number'
                                ? cat.score
                                : Math.round(weightedScore);
                            if (!questionsWithMultipleSpeakers) {
                              return (
                                <p className="text-xs text-gray-100 relative">
                                  Only the candidate&apos;s voice was detected, so Multiple Speaker Detection
                                  adds 0 pts.
                                </p>
                              );
                            }
                            return (
                              <div className="space-y-1.5 text-xs text-gray-100 relative">
                                <p className="font-semibold text-gray-50">
                                  How we reached {finalScore} pts:
                                </p>
                                <p>
                                  • Questions with multiple speakers: {questionsWithMultipleSpeakers} (capped
                                  at 3) → {questionsCapped} × 7.5 ={' '}
                                  <span className="font-semibold">{rawSpeakerScore} raw pts</span>
                                </p>
                                <p>
                                  • Raw total × weight: {rawSpeakerScore} × {speakerWeight} ={' '}
                                  <span className="font-semibold">
                                    {weightedScore.toFixed(1)} pts
                                  </span>{' '}
                                  → rounded to <span className="font-semibold">{finalScore} pts</span> shown
                                  above.
                                </p>
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
